// Section 22 right panel: stocks with capacity bars and 4-season trend arrows, and the
// next-season projection per resource (production minus consumption minus spoilage) from the
// live projection, labelled "assuming no events" (24.8).
import type {
  Factor,
  GameState,
  Metric,
  Projection,
  Resource,
  ResourceFlow,
  Target,
} from '../sim/api';
import { flowFactors } from './explain';
import { memo } from './memo';

export type StockId = Resource | 'seed';
export type Trend = 'up' | 'down' | 'flat';

export interface StockBar {
  id: StockId;
  amount: number;
  capacity: number;
  /** 0..1 of capacity (may exceed 1 when over capacity). */
  fill: number;
  /** Change over the last 4 seasons, or null without history. */
  trend: number | null;
  direction: Trend | null;
  /** The last 4 recorded values, oldest first, for the trend tooltip. */
  recent: number[];
  /** Last season's flows as factors (start, production, consumption, ...). */
  amountFactors: Factor[];
  capacityTarget: Target | null;
}

const ORDER: Resource[] = ['food', 'water', 'wood', 'stone', 'tools', 'medicine', 'cloth', 'fibre'];
const SERIES: Partial<Record<Resource, Metric>> = {
  food: 'food',
  water: 'water',
  wood: 'wood',
  stone: 'stone',
  tools: 'tools',
  medicine: 'medicine',
  cloth: 'cloth',
};
/** A change smaller than this over four seasons reads as flat. */
const FLAT = 0.5;

function directionOf(trend: number | null): Trend | null {
  if (trend === null) return null;
  return trend > FLAT ? 'up' : trend < -FLAT ? 'down' : 'flat';
}

export const stockBars = memo((game: GameState): StockBar[] => {
  const last = game.history[game.history.length - 1];
  const bars: StockBar[] = ORDER.map((r) => {
    const s = game.stocks[r];
    const metric = SERIES[r];
    const series = metric === undefined ? [] : game.series[metric];
    const then = series[series.length - 5];
    const current = series[series.length - 1];
    const trend = then === undefined || current === undefined ? null : current - then;
    return {
      id: r,
      amount: s.amount,
      capacity: s.capacity,
      fill: s.capacity > 0 ? s.amount / s.capacity : 0,
      trend,
      direction: directionOf(trend),
      recent: series.slice(-4),
      amountFactors:
        last === undefined ? [] : flowFactors(last.flows[r], last.stocksStart[r]),
      capacityTarget: { kind: 'capacity', resource: r },
    };
  });
  // Seed has no flows of its own: explain it with last season's seed entries (Section 7).
  const seedFactors: Factor[] = [];
  for (const e of last?.entries ?? []) {
    if (e.kind !== 'seed') continue;
    const sign = e.action === 'reserved' ? 1 : -1;
    seedFactors.push({ source: `seed:${e.action}`, op: 'add', value: sign * e.amount, delta: sign * e.amount });
  }
  bars.splice(1, 0, {
    id: 'seed',
    amount: game.seedReserved,
    capacity: game.stocks.food.capacity,
    fill: game.seedReserved / Math.max(1, game.stocks.food.capacity),
    trend: null,
    direction: null,
    recent: [],
    amountFactors:
      seedFactors.length > 0
        ? seedFactors
        : [{ source: 'seed:held', op: 'base', value: game.seedReserved, delta: game.seedReserved }],
    // Seed counts against food capacity (ADR-0010 item 10).
    capacityTarget: { kind: 'capacity', resource: 'food' },
  });
  return bars;
});

export interface ProjectionRow {
  id: Resource;
  production: number;
  consumption: number;
  spoilage: number;
  /** Production minus consumption minus spoilage (theft is an event; none are assumed). */
  change: number;
  /** Projected stock at the end of next season. */
  end: number;
  /** The rationing engine projects a shortage of this resource next season. */
  short: boolean;
  factors: Factor[];
  /** The end stock: today's stock plus the season's flows. */
  endFactors: Factor[];
}

export interface ProjectionVM {
  /** Last result shown greyed while a new one is computed (24.8). */
  pending: boolean;
  available: boolean;
  error: string | null;
  rows: ProjectionRow[];
  expectedDeaths: number;
  fragility: number | null;
  shortages: string[];
}

const PROJECTED: Resource[] = ['food', 'water', 'wood', 'stone', 'tools', 'medicine', 'cloth'];

function changeOf(f: ResourceFlow): number {
  return f.production - f.consumption - f.spoilage;
}

export const projectionVM = memo(
  (
    game: GameState,
    projection: Projection | null,
    pending: boolean,
    error: string | null,
  ): ProjectionVM => {
    const next = projection?.baseTurn === game.turn ? projection.seasons[0] : undefined;
    if (next === undefined)
      return {
        pending,
        available: false,
        error,
        rows: [],
        expectedDeaths: 0,
        fragility: null,
        shortages: [],
      };
    const warmthShort = next.shortages.includes('warmth');
    return {
      pending,
      available: true,
      error,
      rows: PROJECTED.map((r) => {
        const f = next.flows[r];
        return {
          id: r,
          production: f.production,
          consumption: f.consumption,
          spoilage: f.spoilage,
          change: changeOf(f),
          end: next.stocks[r],
          short:
            (next.shortages as string[]).includes(r) || (r === 'wood' && warmthShort),
          factors: flowFactors({ ...f, theft: 0 }, null),
          endFactors: flowFactors({ ...f, theft: 0 }, game.stocks[r].amount),
        };
      }),
      expectedDeaths: next.expectedDeaths,
      fragility: next.fragility,
      shortages: next.shortages,
    };
  },
);
